import { dashboardApi } from "@/lib/api/dashboard";
import {
  formatCentsAsUsd,
  getDateRangeFromPreset,
  type DateRangePreset,
} from "../utils";

export type OverviewDashboardData = {
  revenue: {
    netSales: number;
    netIncome: number;
    totalRefunds: number;
    totalTransactions: number;
    successfulTransactions: number;
    failedTransactions: number;
    revenueChangePct: number | null;
  };
  enrollments: {
    enrolledToday: number;
    enrolledThisWeek: number;
    totalMatching: number;
  };
  upcoming: {
    totalUpcoming: number;
    totalAmount: number;
    next7Days: { count: number; amount: number };
  };
  tickets: {
    openCount: number;
    totalTickets: number;
    weekOverWeekPct: number | null;
  };
  documents: {
    pending: number;
    uploaded: number;
    rejected: number;
  };
  notifications: {
    unread: number;
    urgent: number;
    high: number;
  };
  alerts: Array<{
    type: string;
    severity: string;
    message: string;
    timestamp?: string;
  }>;
};

function sumTicketOpenCount(
  statusStats: Array<{ _id: string; count: number }> | undefined
): number {
  if (!statusStats?.length) return 0;
  return statusStats
    .filter((s) => s._id !== "closed")
    .reduce((sum, s) => sum + s.count, 0);
}

export async function fetchOverviewDashboardData(
  preset: DateRangePreset
): Promise<OverviewDashboardData> {
  const { startDate, endDate } = getDateRangeFromPreset(preset);
  const res = await dashboardApi.getOverview({ startDate, endDate });
  const data = res.data;

  return {
    revenue: data.revenue,
    enrollments: data.enrollments,
    upcoming: data.upcoming,
    tickets: {
      openCount: sumTicketOpenCount(data.tickets.statusStats),
      totalTickets: data.tickets.totalTickets,
      weekOverWeekPct: data.tickets.weekOverWeekPct ?? null,
    },
    documents: data.documents,
    notifications: {
      unread: data.notifications.unread,
      urgent: data.notifications.urgent,
      high: data.notifications.high,
    },
    alerts: Array.isArray(data.alerts) ? data.alerts : [],
  };
}

export async function downloadDashboardReport(preset: DateRangePreset): Promise<void> {
  const { analyticsApi } = await import("@/lib/api/analytics");
  const { startDate, endDate } = getDateRangeFromPreset(preset);
  const blob = await analyticsApi.exportExcel({
    dateRange: `${startDate},${endDate}`,
    includePaymentData: true,
    includeAnalytics: true,
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dashboard-report-${endDate}.xlsx`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export { formatCentsAsUsd };
